import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Draggable from "react-draggable";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppState";
import { POPUP_SECTIONS, settingIndex } from "../../constant/constant";
import {
  getDraggablePopupBackgroundStyle,
} from "../../helper";
import {
  getStepSectionsForPopup,
  stripShellDimensionsFromCustomCSS,
} from "../../utils/popup-step-sections";
import {
  ackDeselectToParent,
  ackSelectBlockToParent,
  isDeselectBlockMessage,
  parseSelectBlockMessage,
} from "../../utils/preview-selection-messages";
import { shouldShowPopupWatermark } from "../../utils/popup-watermark";
import { COUNTDOWN_DISPLAY_LAYOUT, resolveCountdownDisplay } from "../../utils/countdownDisplay";
import TextBlock from "../../components/Preview/Draggable/Block/TextBlock";
import CountdownBlock from "../../components/Preview/Draggable/Block/CountdownBlock";
import PopupWatermark from "../../components/Preview/PopupWatermark";
import AiRecommendationRibbon from "./components/AiRecommendationRibbon";
import ProductRecommendationPitchLine from "../product-recommendation/components/ProductRecommendationPitchLine";
import { isPitchLayoutSection } from "../../../shared/pitch-layout-display.js";
import useAiRecommendationCopy from "./hooks/useAiRecommendationCopy";
import {
  isPopupRuntimeContentLoading,
} from "../../utils/popup-runtime-content-loading";
import AiSocialProofBar from "./components/AiSocialProofBar";
import AiTrustSignalsRow from "./components/AiTrustSignalsRow";
import AiProductOfferSection from "./components/AiProductOfferSection";
import AiBrandAttribution from "./components/AiBrandAttribution";
import AiCatalogCtaButton from "./components/AiCatalogCtaButton";
import {
  getAiRecommendationUpsellLayoutOptions,
  indexAiRecommendationUpsellSections,
} from "./mapAiRecommendationUpsellSchema";
import {
  resolvePopupThemeBorderRadius,
  formatBorderRadiusCss,
} from "../../utils/resolve-popup-theme-border-radius.js";
import {
  buildPopupThemeCssVariables,
  resolvePopupThemeTokens,
} from "../../utils/resolve-popup-theme-tokens.js";
import {
  isSectionVisible,
  isSectionVisibleForViewport,
} from "../../../shared/section-visibility.js";
import { getPopupLayoutDefinition } from "../../popup-layouts/index.js";
import styles from "./AiRecommendationUpsellPopup.module.css";

const DEFAULT_SHELL_DESKTOP_WIDTH_PX = 625;

function resolveShellFontFamily(popupData, activeStep) {
  const step = popupData?.schema?.[activeStep];
  const product = step?.section?.find((s) => s?.type === POPUP_SECTIONS.PRODUCT);
  const fromProduct =
    product?.setting?.customCSS?.desktop?.fontFamily ||
    product?.setting?.customCSS?.mobile?.fontFamily;
  const fromPopup =
    step?.popupSettings?.customCSS?.desktop?.fontFamily ||
    step?.popupSettings?.customCSS?.mobile?.fontFamily;
  const family = fromProduct || fromPopup;
  if (!family || typeof family !== "string") {
    return '"Aimbrill-Poppins", system-ui, sans-serif';
  }
  return family.includes(",") ? family : `"${family}", system-ui, sans-serif`;
}

function PreviewSlot({
  children,
  isSelected,
  onClick,
  className = "",
}) {
  return (
    <div
      className={`${styles.slot} ${isSelected ? styles.slotSelected : ""} ${className}`.trim()}
      onClick={(e) => {
        const target = e.target;
        const isInputField =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.contentEditable === "true" ||
          target.closest("input, textarea, select, [contenteditable]");
        const isInteractive =
          target.tagName === "BUTTON" ||
          target.closest("button, a, [role='button']");
        if (!isInputField && !isInteractive) onClick?.(e);
      }}
    >
      {children}
    </div>
  );
}

const AiRecommendationUpsellPopup = ({ popupData, popupState, setPopupState }) => {
  const {
    appState,
    isMobile: viewportIsMobile,
    previewDeviceIsMobile,
    clickTriggerEvents,
    setClickTriggerEvents,
    popupGlobalData,
  } = useApp();
  const isMobile =
    appState?.is_preview_mode && typeof previewDeviceIsMobile === "boolean"
      ? previewDeviceIsMobile
      : viewportIsMobile;

  const location = useLocation();
  const navigate = useNavigate();
  const popupRef = useRef(null);
  const finishCloseRef = useRef(null);
  const closeAnimationDoneRef = useRef(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);

  const popupType = popupData?.popUpType;
  const popupId = popupData?._id;

  const activeStep = useMemo(() => {
    const fromPopupState = popupState?.activeStep;
    if (typeof fromPopupState === "number" && !Number.isNaN(fromPopupState)) {
      return fromPopupState;
    }
    if (
      appState?.is_preview_mode &&
      typeof appState?.activeStep === "number" &&
      !Number.isNaN(appState.activeStep)
    ) {
      return appState.activeStep;
    }
    return 0;
  }, [popupState?.activeStep, appState?.is_preview_mode, appState?.activeStep]);

  const currentParentData = useMemo(
    () => popupGlobalData?.[`${popupId}_${popupType}`] ?? {},
    [popupGlobalData, popupId, popupType]
  );

  const [selectedBlock, setSelectedBlock] = useState({});
  const [offerProducts, setOfferProducts] = useState([]);
  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const stepSections = useMemo(
    () => getStepSectionsForPopup(popupData, activeStep),
    [popupData, activeStep]
  );

  const mapped = useMemo(
    () => indexAiRecommendationUpsellSections(stepSections),
    [stepSections]
  );

  const layoutOptions = useMemo(
    () => getAiRecommendationUpsellLayoutOptions(popupData),
    [popupData]
  );

  const offerProductData = useMemo(
    () => offerProducts?.[0] || mapped.product?.section?.products?.[0] || null,
    [offerProducts, mapped.product?.section?.products]
  );

  const aiRecommendationCopy = useAiRecommendationCopy(mapped.aiBanner, popupData);
  const runtimeContentLoading =
    !appState?.is_preview_mode && isPopupRuntimeContentLoading(popupData);
  const pitchLoading = aiRecommendationCopy.loading;

  const popupSettings = popupData?.schema?.[activeStep]?.popupSettings || {};
  const shellCss =
    popupSettings?.customCSS?.[isMobile ? "mobile" : "desktop"] || {};

  const shellFontFamily = useMemo(
    () => resolveShellFontFamily(popupData, activeStep),
    [popupData, activeStep]
  );

  const shellBorderRadiusPx = useMemo(
    () =>
      resolvePopupThemeBorderRadius(popupData, {
        activeStep,
        isMobile,
      }),
    [popupData, activeStep, isMobile]
  );

  const themeTokens = useMemo(
    () =>
      resolvePopupThemeTokens(popupData, {
        activeStep,
        isMobile,
      }),
    [popupData, activeStep, isMobile]
  );

  const themeCssVariables = useMemo(
    () => buildPopupThemeCssVariables(themeTokens),
    [themeTokens]
  );

  const shellStyle = useMemo(() => {
    const stripped = stripShellDimensionsFromCustomCSS(shellCss);
    const radiusCss = formatBorderRadiusCss(shellBorderRadiusPx);
    const resolvedWidth =
      isMobile && (shellCss.widthUnit === "%" || shellCss.width === "100%")
        ? "100%"
        : shellCss.widthUnit === "%"
          ? shellCss.width
          : `${shellCss.width || DEFAULT_SHELL_DESKTOP_WIDTH_PX}px`;
    const mobileDrawerShell = isMobile;
    return {
      ...getDraggablePopupBackgroundStyle(popupSettings, isMobile),
      ...stripped,
      ...themeCssVariables,
      "--ai-upsell-border-radius": radiusCss,
      "--we-radius": radiusCss,
      borderRadius: mobileDrawerShell ? "20px 20px 0 0" : radiusCss,
      width: mobileDrawerShell ? "100%" : resolvedWidth,
      maxWidth: "100%",
      ...(themeTokens.fontFamily ? { fontFamily: themeTokens.fontFamily } : {}),
      color: themeTokens.text,
      boxShadow: mobileDrawerShell
        ? "0 -8px 32px rgba(15, 23, 42, 0.14)"
        : stripped.boxShadow || "0 28px 56px rgba(15, 23, 42, 0.14)",
      animation: mobileDrawerShell ? "none" : stripped.animation,
    };
  }, [
    popupSettings,
    isMobile,
    shellCss,
    shellBorderRadiusPx,
    shellFontFamily,
    themeCssVariables,
    themeTokens.fontFamily,
    themeTokens.text,
  ]);

  const handleBlockClick = useCallback(
    (type, index) => {
      if (!appState?.is_preview_mode) return;
      const prev = selectedBlockRef.current;
      if (prev.type === type && prev.index === index) return;
      ackSelectBlockToParent({ type, index });
      setSelectedBlock({ type, index });
    },
    [appState?.is_preview_mode]
  );

  const handleOutSideBlockClick = useCallback(() => {
    setSelectedBlock({});
    if (appState?.is_preview_mode) {
      ackDeselectToParent();
    }
  }, [appState?.is_preview_mode]);

  useEffect(() => {
    if (!appState?.is_preview_mode) return undefined;

    const handleOutsideClick = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleOutSideBlockClick();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [appState?.is_preview_mode, handleOutSideBlockClick]);

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      if (isDeselectBlockMessage(data)) {
        setSelectedBlock({});
        return;
      }

      if (data.event === "sectionCreated") {
        const { type, index } = data;
        if (index === settingIndex) {
          handleOutSideBlockClick();
        } else if (typeof index === "number" && index !== settingIndex) {
          setSelectedBlock({ type, index });
          handleBlockClick(type, index);
        }
        return;
      }

      const selection = parseSelectBlockMessage(data);
      if (!selection) return;

      const { type, index, subIndex } = selection;
      if (index === settingIndex || index === -123) {
        handleOutSideBlockClick();
        return;
      }
      if (typeof subIndex === "number" && subIndex >= 0) {
        setSelectedBlock({});
        return;
      }
      setSelectedBlock({ type, index });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleBlockClick, handleOutSideBlockClick]);

  const finishClose = useCallback(
    async (callAddToCartEvents = true) => {
      setPopupState((prev) => ({
        ...prev,
        [`${popupType}_${popupId}`]: {
          ...prev?.[`${popupType}_${popupId}`],
          openPopup: false,
          drawerClosing: false,
        },
      }));

      if (location?.hash !== "") {
        navigate(`${location?.pathname}`);
      }

      if (
        clickTriggerEvents?.[`${popupType}-${popupId}`] &&
        callAddToCartEvents &&
        typeof clickTriggerEvents?.[`${popupType}-${popupId}`] === "function"
      ) {
        clickTriggerEvents?.[`${popupType}-${popupId}`]();
        setTimeout(() => {
          setClickTriggerEvents((prev) => {
            const temp = { ...prev };
            delete temp[`${popupType}-${popupId}`];
            return temp;
          });
        }, 100);
      }
    },
    [
      setPopupState,
      location,
      navigate,
      popupType,
      popupId,
      popupData,
      clickTriggerEvents,
      setClickTriggerEvents,
    ]
  );
  finishCloseRef.current = finishClose;

  const handleClose = useCallback(
    async (callAddToCartEvents = true) => {
      if (isDrawerClosing) return;

      if (isMobile) {
        setIsDrawerClosing(true);
        setPopupState((prev) => ({
          ...prev,
          [`${popupType}_${popupId}`]: {
            ...prev?.[`${popupType}_${popupId}`],
            drawerClosing: true,
          },
        }));
        return;
      }

      await finishClose(callAddToCartEvents);
    },
    [
      isMobile,
      isDrawerClosing,
      finishClose,
      setPopupState,
      popupType,
      popupId,
    ]
  );

  const handleDrawerAnimationEnd = useCallback((event) => {
    if (event.target !== event.currentTarget) return;
    const animationName = String(event.animationName || "");
    if (!animationName.includes("SlideDown")) return;
    if (closeAnimationDoneRef.current) return;
    closeAnimationDoneRef.current = true;
    setIsDrawerClosing(false);
    finishCloseRef.current?.();
  }, []);

  useEffect(() => {
    if (!isDrawerClosing) {
      closeAnimationDoneRef.current = false;
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (closeAnimationDoneRef.current) return;
      closeAnimationDoneRef.current = true;
      setIsDrawerClosing(false);
      finishCloseRef.current?.();
    }, 380);

    return () => window.clearTimeout(timer);
  }, [isDrawerClosing]);

  const productAutoFocusStepRef = useRef(null);

  useEffect(() => {
    productAutoFocusStepRef.current = null;
  }, [activeStep]);

  useEffect(() => {
    if (!appState?.is_preview_mode) return;
    if (productAutoFocusStepRef.current === activeStep) return;

    const productIndex = mapped.product?.index ?? -1;
    if (productIndex < 0) return;

    productAutoFocusStepRef.current = activeStep;
    handleBlockClick(POPUP_SECTIONS.PRODUCT, productIndex);
  }, [activeStep, mapped.product?.index, appState?.is_preview_mode, handleBlockClick]);

  useEffect(() => {
    const root = document.querySelector("#popup-preview-shadow-root");
    if (root) {
      root.style.zIndex = `${popupSettings?.zIndex ?? 9999999999999}`;
    }
  }, [activeStep, popupSettings?.zIndex]);

  const hasInlineBrandAttribution = Boolean(mapped.brandAttribution);

  const showFloatingWatermark = useMemo(
    () =>
      !hasInlineBrandAttribution &&
      shouldShowPopupWatermark(
        popupData,
        activeStep,
        appState?.watermark,
        appState?.is_preview_mode,
        appState?.shop
      ),
    [
      hasInlineBrandAttribution,
      popupData,
      activeStep,
      appState?.watermark,
      appState?.is_preview_mode,
      appState?.shop,
    ]
  );

  const countdownDisplay = mapped.countdown?.section
    ? resolveCountdownDisplay(mapped.countdown.section, popupData)
    : null;
  const showCountdownBar =
    mapped.countdown &&
    isSectionVisible(mapped.countdown.section) &&
    countdownDisplay?.layout === COUNTDOWN_DISPLAY_LAYOUT.BAR;

  const isSelected = (entry) =>
    entry &&
    selectedBlock.type === entry.section?.type &&
    selectedBlock.index === entry.index;

  const shellDragDisabled = useMemo(() => {
    if (appState?.is_preview_mode) return true;

    const gs = popupData?.generalSettings || {};
    const layoutDef = getPopupLayoutDefinition(popupData);
    const lockDraggable = gs.lockDraggable ?? layoutDef.lockDraggable ?? true;
    if (lockDraggable) return true;

    const liveDrag = gs.draggableInLiveMode;
    return !(liveDrag?.enabled && liveDrag?.step === activeStep);
  }, [appState?.is_preview_mode, popupData, activeStep]);

  const shellContent = (
    <div
      className={`${styles.shellWrap} ${isMobile ? styles.shellWrapDrawer : ""} ${
        isMobile && isDrawerClosing ? styles.shellWrapDrawerClosing : ""
      }`.trim()}
      ref={popupRef}
      style={isMobile ? undefined : { maxWidth: "100%" }}
      onAnimationEnd={isMobile ? handleDrawerAnimationEnd : undefined}
    >
      <button
        type="button"
        className={`${styles.shellClose} ${isMobile ? styles.shellCloseDrawer : ""}`.trim()}
        aria-label="Close popup"
        onClick={(e) => {
          e.stopPropagation();
          if (appState?.is_preview_mode) return;
          handleClose();
        }}
        style={
          appState?.is_preview_mode
            ? { pointerEvents: "none", cursor: "default" }
            : undefined
        }
      >
        ×
      </button>
      <div
        className={`${styles.shell} ${isMobile ? styles.shellDrawer : ""}`.trim()}
        style={shellStyle}
        data-testid="ai-recommendation-upsell-popup"
      >
            {showCountdownBar ? (
              <PreviewSlot
                className={styles.countdownSlot}
                isSelected={isSelected(mapped.countdown)}
                onClick={() =>
                  handleBlockClick(
                    mapped.countdown.section.type,
                    mapped.countdown.index
                  )
                }
              >
                <CountdownBlock
                  popupData={popupData}
                  section={mapped.countdown.section}
                />
              </PreviewSlot>
            ) : null}

            {mapped.aiBanner && isSectionVisible(mapped.aiBanner.section) ? (
              <PreviewSlot
                className={
                  isPitchLayoutSection(mapped.aiBanner.section)
                    ? styles.pitchSlot
                    : `${styles.insetHorizontal} ${styles.ribbonSlot}`.trim()
                }
                isSelected={isSelected(mapped.aiBanner)}
                onClick={() =>
                  handleBlockClick(
                    mapped.aiBanner.section.type,
                    mapped.aiBanner.index
                  )
                }
              >
                {isPitchLayoutSection(mapped.aiBanner.section) ? (
                  <ProductRecommendationPitchLine
                    section={mapped.aiBanner.section}
                    popupData={popupData}
                    activeStep={activeStep}
                    index={mapped.aiBanner.index}
                    displayText={aiRecommendationCopy.displayText}
                    loading={pitchLoading}
                    isMobile={isMobile}
                  />
                ) : (
                  <AiRecommendationRibbon
                    section={mapped.aiBanner.section}
                    popupData={popupData}
                    activeStep={activeStep}
                    index={mapped.aiBanner.index}
                    parentData={currentParentData}
                    displayText={aiRecommendationCopy.displayText}
                    loading={pitchLoading}
                    isMobile={isMobile}
                  />
                )}
              </PreviewSlot>
            ) : null}

            <div
              className={styles.body}
              onClick={(e) => {
                const target = e.target;
                const isInputField =
                  target.tagName === "INPUT" ||
                  target.tagName === "TEXTAREA" ||
                  target.tagName === "SELECT" ||
                  target.contentEditable === "true" ||
                  target.closest("input, textarea, select, [contenteditable]");
                if (!isInputField && e.target === e.currentTarget) {
                  handleOutSideBlockClick();
                }
              }}
            >
              {mapped.product && isSectionVisible(mapped.product.section) ? (
                <PreviewSlot
                  isSelected={isSelected(mapped.product)}
                  onClick={() =>
                    handleBlockClick(
                      mapped.product.section.type,
                      mapped.product.index
                    )
                  }
                >
                  <AiProductOfferSection
                    layoutOptions={layoutOptions}
                    popupData={popupData}
                    popupState={popupState}
                    setPopupState={setPopupState}
                    section={mapped.product.section}
                    activeStep={activeStep}
                    index={mapped.product.index}
                    handleClose={handleClose}
                    onProductsChange={setOfferProducts}
                    isMobile={isMobile}
                    isPreviewMode={appState?.is_preview_mode}
                    contentLoading={runtimeContentLoading}
                  />
                </PreviewSlot>
              ) : null}

              {mapped.socialProof && isSectionVisible(mapped.socialProof.section) ? (
                <PreviewSlot
                  isSelected={isSelected(mapped.socialProof)}
                  onClick={() =>
                    handleBlockClick(
                      mapped.socialProof.section.type,
                      mapped.socialProof.index
                    )
                  }
                >
                  <AiSocialProofBar
                    section={mapped.socialProof.section}
                    text={mapped.socialProof.section?.text}
                    showAvatars={layoutOptions.showSocialProofAvatars !== false}
                  />
                </PreviewSlot>
              ) : null}

              <div className={styles.ctaStack}>
                {mapped.primaryCta &&
                isSectionVisible(mapped.primaryCta.section) &&
                isSectionVisibleForViewport(mapped.primaryCta.section, isMobile) ? (
                  <PreviewSlot
                    isSelected={isSelected(mapped.primaryCta)}
                    onClick={() =>
                      handleBlockClick(
                        mapped.primaryCta.section.type,
                        mapped.primaryCta.index
                      )
                    }
                  >
                    <AiCatalogCtaButton
                      popupData={popupData}
                      popupState={popupState}
                      setPopupState={setPopupState}
                      section={mapped.primaryCta.section}
                      handleClose={handleClose}
                      index={mapped.primaryCta.index}
                      productIndex={0}
                      productData={offerProductData}
                      parentData={currentParentData}
                    />
                  </PreviewSlot>
                ) : null}

                {mapped.secondaryCta &&
                isSectionVisible(mapped.secondaryCta.section) &&
                isSectionVisibleForViewport(mapped.secondaryCta.section, isMobile) ? (
                  <PreviewSlot
                    isSelected={isSelected(mapped.secondaryCta)}
                    onClick={() =>
                      handleBlockClick(
                        mapped.secondaryCta.section.type,
                        mapped.secondaryCta.index
                      )
                    }
                  >
                    <AiCatalogCtaButton
                      popupData={popupData}
                      popupState={popupState}
                      setPopupState={setPopupState}
                      section={mapped.secondaryCta.section}
                      handleClose={handleClose}
                      index={mapped.secondaryCta.index}
                      productIndex={0}
                      productData={offerProductData}
                      parentData={currentParentData}
                    />
                  </PreviewSlot>
                ) : null}
              </div>

              {mapped.trust && isSectionVisible(mapped.trust.section) ? (
                <PreviewSlot
                  className={styles.trustRow}
                  isSelected={isSelected(mapped.trust)}
                  onClick={() =>
                    handleBlockClick(
                      mapped.trust.section.type,
                      mapped.trust.index
                    )
                  }
                >
                  <AiTrustSignalsRow
                    section={mapped.trust.section}
                    text={mapped.trust.section?.text}
                    isMobile={isMobile}
                  />
                </PreviewSlot>
              ) : null}
            </div>

            {mapped.brandAttribution && isSectionVisible(mapped.brandAttribution.section) ? (
              <PreviewSlot
                className={`${styles.insetHorizontal} ${styles.footerRow}`.trim()}
                isSelected={isSelected(mapped.brandAttribution)}
                onClick={() =>
                  handleBlockClick(
                    mapped.brandAttribution.section.type,
                    mapped.brandAttribution.index
                  )
                }
              >
                <AiBrandAttribution section={mapped.brandAttribution.section} />
              </PreviewSlot>
            ) : null}

            {showFloatingWatermark ? (
              <PopupWatermark
                popupData={popupData}
                activeStep={activeStep}
                visible={showFloatingWatermark}
              />
            ) : null}
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? (
        shellContent
      ) : (
        <Draggable disabled={shellDragDisabled}>{shellContent}</Draggable>
      )}
    </>
  );
};

export default AiRecommendationUpsellPopup;
